import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, ChevronDown, ChevronRight } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Reveal, Eyebrow } from "../components/Reveal";
import { QuoteForm } from "../sections/QuoteForm";
import { useSiteContent } from "../App";
import { PRODUCT_PAGES, TUBE_ROUTES } from "../data/productPages";

const NAV_ITEMS = [
  ["Overview", "p-overview"],
  ["Specification", "p-spec"],
  ["Print & Finish", "p-print"],
  ["FAQs", "p-faqs"],
  ["Quote", "p-quote"],
];

const scrollToId = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

const Faq = ({ q, a, i }) => {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="border-b border-white/8" data-testid={`faq-item-${i}`}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 py-5 text-left"
        data-testid={`faq-toggle-${i}`}
      >
        <span className="text-[15px] font-medium text-[#F5F3EF]">{q}</span>
        <ChevronDown size={16} className={`shrink-0 text-[#D4AF37] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="pb-5 text-sm text-[#A1A8B3] leading-relaxed max-w-2xl">{a}</p>}
    </div>
  );
};

export default function ProductPage() {
  const { slug } = useParams();
  const page = PRODUCT_PAGES[slug];
  const { data } = useSiteContent();
  const [stickyHidden, setStickyHidden] = useState(false);

  useEffect(() => {
    if (!page) return;
    document.title = page.seoTitle;
    const meta = document.querySelector('meta[name="description"]');
    const prev = meta?.getAttribute("content");
    meta?.setAttribute("content", page.metaDescription);
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "breadcrumb-ld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${window.location.origin}/` },
        { "@type": "ListItem", position: 2, name: "Printed Cardboard Tubes", item: `${window.location.origin}/#products` },
        { "@type": "ListItem", position: 3, name: page.h1, item: `${window.location.origin}/printed-cardboard-tubes/${page.slug}` },
      ],
    });
    document.head.appendChild(ld);
    return () => {
      document.title = "Custom Printed Cardboard Tubes | TubeBoxesDirect.com";
      if (meta && prev) meta.setAttribute("content", prev);
      document.getElementById("breadcrumb-ld")?.remove();
    };
  }, [page]);

  useEffect(() => {
    const target = document.getElementById("p-quote");
    const footer = document.querySelector("footer");
    if (!target) return;
    const obs = new IntersectionObserver(
      (entries) => setStickyHidden(entries.some((e) => e.isIntersecting)),
      { rootMargin: "-10% 0px 0px 0px" }
    );
    obs.observe(target);
    if (footer) obs.observe(footer);
    return () => obs.disconnect();
  }, [page]);

  if (!page) return <Navigate to="/" replace />;

  const content = data || {};
  const products = content.products || [];
  const thisProduct = products.find((p) => p.id === page.styleId) || {};
  const others = products.filter((p) => p.id !== page.styleId);

  return (
    <div data-testid={`product-page-${page.slug}`}>
      <Header announcement={content.announcement} />

      <main className="pt-40 pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <nav aria-label="Breadcrumb" className="mb-10" data-testid="breadcrumbs">
            <ol className="flex flex-wrap items-center gap-1.5 font-mono-spec text-[10px] uppercase tracking-[0.2em] text-[#666F7D]">
              <li><Link to="/" className="hover:text-[#D9A76A] transition-colors" data-testid="crumb-home">Home</Link></li>
              <li aria-hidden="true"><ChevronRight size={11} className="inline text-[#666F7D]" /></li>
              <li><a href="/#products" className="hover:text-[#D9A76A] transition-colors" data-testid="crumb-tubes">Printed Cardboard Tubes</a></li>
              <li aria-hidden="true"><ChevronRight size={11} className="inline text-[#666F7D]" /></li>
              <li aria-current="page" className="text-[#D9A76A]" data-testid="crumb-current">{page.styleName}</li>
            </ol>
          </nav>

          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-2xl overflow-hidden border border-white/8 bg-[#EFE7D8] order-first lg:order-last"
            >
              <img
                src={thisProduct.image_url}
                alt={page.imageAlt}
                className="w-full h-[340px] sm:h-[460px] object-contain p-6"
                data-testid="product-hero-image"
              />
            </motion.div>
            <div>
              <Eyebrow>Printed cardboard tubes</Eyebrow>
              <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#F5F3EF] leading-[1.12]" data-testid="product-h1">
                {page.h1}
              </h1>
              <p className="mt-6 text-base sm:text-lg text-[#A1A8B3] font-light leading-relaxed" data-testid="product-intro">{page.intro}</p>
              <ul className="mt-7 space-y-3">
                {page.keyPoints.map((k) => (
                  <li key={k} className="flex items-start gap-3 text-sm text-[#A1A8B3]">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-[#D4AF37]/15 flex items-center justify-center shrink-0">
                      <Check size={11} className="text-[#D4AF37]" />
                    </span>
                    {k}
                  </li>
                ))}
              </ul>
              <div className="mt-9 flex flex-wrap gap-4">
                <button onClick={() => scrollToId("p-quote")} data-testid="product-primary-cta" className="gold-btn text-[#0B0D10] font-semibold text-sm px-7 py-4 rounded-full flex items-center gap-2">
                  {page.primaryCta} <ArrowRight size={15} />
                </button>
                <a href="/#sizes" data-testid="product-secondary-cta" className="ghost-btn border border-white/15 text-[#F5F3EF] text-sm font-medium px-7 py-4 rounded-full">
                  {page.secondaryCta}
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky top-[72px] z-30 mt-16 bg-[#0B0D10]/90 backdrop-blur-xl border-y border-white/8" data-testid="product-subnav">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 flex gap-1 overflow-x-auto scrollbar-thin">
            {NAV_ITEMS.map(([label, id]) => (
              <button
                key={id}
                onClick={() => scrollToId(id)}
                data-testid={`subnav-${id}`}
                className="shrink-0 text-[12px] font-medium text-[#A1A8B3] hover:text-[#E6CA65] px-4 py-4 border-b-2 border-transparent hover:border-[#D4AF37]/60 transition-colors"
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <section id="p-overview" className="scroll-mt-40 pt-20" data-testid="section-overview">
            <div className="grid gap-14 lg:grid-cols-2">
              {page.overviewSections.map((s) => (
                <Reveal key={s.heading}>
                  <h2 className="font-serif-display text-xl sm:text-2xl font-medium text-[#F5F3EF] leading-snug">{s.heading}</h2>
                  {s.paragraphs.map((p, i) => (
                    <p key={i} className="mt-4 text-[15px] text-[#A1A8B3] leading-relaxed">{p}</p>
                  ))}
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.1}>
              <h2 className="mt-20 font-mono-spec text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]/90">{page.applicationsTitle}</h2>
            </Reveal>
            <div className="mt-8 grid gap-px bg-white/5 border border-white/5 rounded-2xl overflow-hidden sm:grid-cols-2 lg:grid-cols-4">
              {page.applications.map((a, i) => (
                <Reveal key={a.title} delay={i * 0.06} className="h-full">
                  <div className="bg-[#14171D] p-7 h-full" data-testid={`application-${i}`}>
                    <h3 className="font-serif-display text-lg text-[#F5F3EF]">{a.title}</h3>
                    <p className="mt-2.5 text-[13px] text-[#A1A8B3] leading-relaxed">{a.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>

          <section id="p-spec" className="scroll-mt-40 pt-24" data-testid="section-spec">
            <Reveal>
              <Eyebrow>Specification</Eyebrow>
              <h2 className="font-serif-display text-2xl sm:text-3xl font-medium tracking-tight text-[#F5F3EF]">Construction & commercial details</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-9 border border-white/8 rounded-2xl overflow-hidden overflow-x-auto scrollbar-thin">
                <table className="w-full text-sm min-w-[560px]" data-testid="spec-table">
                  <tbody>
                    {page.specRows.map(([label, value], i) => (
                      <tr key={label} className={i % 2 === 0 ? "bg-[#14171D]" : "bg-[#10131A]"}>
                        <th scope="row" className="text-left font-mono-spec text-[10px] uppercase tracking-[0.18em] text-[#D9A76A] font-medium px-6 py-4 align-top w-44 sm:w-56">{label}</th>
                        <td className="px-6 py-4 text-[#A1A8B3] leading-relaxed">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {page.specNote && <p className="mt-5 text-[13px] text-[#666F7D] leading-relaxed max-w-3xl">{page.specNote}</p>}
              <p className="mt-3 text-[13px] text-[#666F7D] leading-relaxed max-w-3xl">
                Dispatch times refer to production. Delivery time is additional and depends on destination.
              </p>
            </Reveal>

            {(page.extraSections || []).map((s) => (
              <Reveal key={s.heading} className="mt-14">
                <h3 className="font-serif-display text-xl sm:text-2xl font-medium text-[#F5F3EF]">{s.heading}</h3>
                {s.paragraphs.map((p, i) => (
                  <p key={i} className="mt-4 text-[15px] text-[#A1A8B3] leading-relaxed max-w-3xl">{p}</p>
                ))}
              </Reveal>
            ))}
          </section>

          <section id="p-print" className="scroll-mt-40 pt-24" data-testid="section-print">
            <Reveal>
              <Eyebrow>{page.printFinish.heading}</Eyebrow>
              <div className="max-w-3xl">
                {page.printFinish.paragraphs.map((p, i) => (
                  <p key={i} className={`${i === 0 ? "" : "mt-4"} text-[15px] text-[#A1A8B3] leading-relaxed`}>{p}</p>
                ))}
              </div>
              <p className="mt-6 text-[13px] text-[#666F7D] leading-relaxed max-w-3xl">
                Finish availability depends on the selected material and specification — options are confirmed with your quotation.
              </p>
            </Reveal>
          </section>

          <section id="p-faqs" className="scroll-mt-40 pt-24" data-testid="section-faqs">
            <Reveal>
              <Eyebrow>FAQs</Eyebrow>
              <h2 className="font-serif-display text-2xl sm:text-3xl font-medium tracking-tight text-[#F5F3EF]">Questions, answered</h2>
              <div className="mt-8 max-w-3xl border-t border-white/8">
                {page.faqs.map((f, i) => (
                  <Faq key={f.q} q={f.q} a={f.a} i={i} />
                ))}
              </div>
            </Reveal>
          </section>

          <section className="pt-24" data-testid="section-related">
            <Reveal>
              <Eyebrow>Other tube styles</Eyebrow>
            </Reveal>
            <div className="mt-8 grid gap-6 sm:grid-cols-2">
              {others.map((p) => (
                <Reveal key={p.id}>
                  <div className="product-card border border-white/8 bg-[#14171D] rounded-2xl overflow-hidden flex flex-col sm:flex-row" data-testid={`related-${p.id}`}>
                    <Link to={TUBE_ROUTES[p.id]} className="sm:w-44 shrink-0 bg-[#EFE7D8]" data-testid={`related-${p.id}-image-link`}>
                      <img src={p.image_url} alt={`${p.name} cardboard tube`} className="w-full h-40 sm:h-full object-contain p-3" loading="lazy" />
                    </Link>
                    <div className="p-6 flex flex-col">
                      <h3 className="font-serif-display text-lg text-[#F5F3EF]">
                        <Link to={TUBE_ROUTES[p.id]} className="hover:text-[#E6CA65] transition-colors" data-testid={`related-${p.id}-title-link`}>{p.name}</Link>
                      </h3>
                      <p className="mt-2 text-[13px] text-[#A1A8B3] leading-relaxed flex-1">{p.description}</p>
                      <Link to={TUBE_ROUTES[p.id]} data-testid={`related-${p.id}-explore`} className="mt-4 inline-flex items-center gap-2 text-[13px] font-medium text-[#D4AF37] hover:text-[#E6CA65] transition-colors">
                        Explore <ArrowRight size={13} />
                      </Link>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>

          <section id="p-quote" className="scroll-mt-40 pt-24" data-testid="section-quote">
            <Reveal>
              <div className="text-center max-w-2xl mx-auto mb-10">
                <h2 className="font-serif-display text-2xl sm:text-3xl font-medium tracking-tight text-[#F5F3EF]">{page.finalCta.heading}</h2>
                <p className="mt-4 text-[15px] text-[#A1A8B3] leading-relaxed">{page.finalCta.text}</p>
              </div>
              <QuoteForm
                content={{ ...content, products, sizes: content.sizes || [], finishes: content.finishes || [], moq: content.moq || 250 }}
                initialProduct={page.styleId}
                sourcePage={`printed-cardboard-tubes/${page.slug}`}
                submitLabel={page.finalCta.button}
                embedded
              />
            </Reveal>
          </section>
        </div>
      </main>

      <Footer email={content.contact_email || "sales@tubeboxesdirect.com"} />

      <motion.div
        initial={false}
        animate={{ y: stickyHidden ? 90 : 0, opacity: stickyHidden ? 0 : 1 }}
        transition={{ duration: 0.3 }}
        className="lg:hidden fixed bottom-4 inset-x-4 z-40 pointer-events-none"
        data-testid="mobile-sticky-quote"
      >
        <button
          onClick={() => scrollToId("p-quote")}
          className="pointer-events-auto gold-btn w-full text-[#0B0D10] font-semibold text-sm py-4 rounded-full flex items-center justify-center gap-2 shadow-2xl"
          data-testid="mobile-sticky-quote-btn"
        >
          {page.primaryCta} <ArrowRight size={15} />
        </button>
      </motion.div>
    </div>
  );
}
